import { destinationWords } from "../../shared/schedule-destinations";
import type { ScheduleDestinationDTO, ScheduleDestinationInput, ScheduleDTO } from "../../shared/types";

/** Where a schedule's answer goes, as the dialog edits it: the people it DMs, the people whose chat it reaches, the channels. */
export type DestinationDraft = { dms: string[]; webs: string[]; channels: { channel: string; label: string }[] };

export function draftFrom(destinations: ScheduleDestinationDTO[]): DestinationDraft {
  return {
    dms: destinations.filter((d) => d.kind === "slack_dm" && d.userId).map((d) => d.userId!),
    webs: destinations.filter((d) => d.kind === "web" && d.userId).map((d) => d.userId!),
    channels: destinations.filter((d) => d.kind === "slack_channel").map((d) => ({ channel: d.channel ?? "", label: d.label ?? "" })),
  };
}

/** What a save sends: a channel row left empty is no channel; a typed id is trimmed and upper-cased, as Slack writes them. */
export function destinationsInput(draft: DestinationDraft): ScheduleDestinationInput[] {
  return [
    ...draft.dms.map((userId) => ({ kind: "slack_dm" as const, userId })),
    ...draft.webs.map((userId) => ({ kind: "web" as const, userId })),
    ...draft.channels
      .filter((c) => c.channel.trim())
      .map((c) => ({ kind: "slack_channel" as const, channel: c.channel.trim().toUpperCase(), label: c.label.trim() || null })),
  ];
}

/** Whether two drafts send the same thing, so an edit sends `destinations` only when they changed. */
export function sameDestinations(a: DestinationDraft, b: DestinationDraft): boolean {
  return JSON.stringify(destinationsInput(a)) === JSON.stringify(destinationsInput(b));
}

/** Whether its answer reaches anyone but its owner: then "Run now" asks first, naming them. */
export function othersOf(schedule: Pick<ScheduleDTO, "destinations" | "ownerUserId">): ScheduleDestinationDTO[] {
  return schedule.destinations.filter((d) => !(d.kind === "slack_dm" && d.userId === schedule.ownerUserId));
}

/** One destination for the person looking: "your Slack DM" for their own, otherwise its words. */
export function destinationFor(d: ScheduleDestinationDTO, meId: string): string {
  if (d.userId === meId) return d.kind === "slack_dm" ? "your Slack DM" : "your web chat";
  return destinationWords(d);
}

/** How the last firing's delivery here went, in a few words: "sent 2h ago", "failed: not_in_channel". */
export function deliveryWords(d: ScheduleDestinationDTO, ago: (iso: string) => string): string | null {
  const last = d.lastDelivery;
  if (!last) return null;
  if (last.status === "sent") return `sent ${ago(last.at)}`;
  if (last.status === "pending") return "on its way";
  return `${last.status}${last.detail ? `: ${last.detail}` : ""}`;
}
